import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Layout,
    Flex,
    Grid,
    Tabs,
    Table,
    Avatar,
    Tag,
    Space,
    Typography,
    Modal,
} from "antd";
import { ExclamationCircleFilled, UserOutlined } from "@ant-design/icons";
import { EditIcon, TrashIcon } from "../../assets/CustomIcon";

import { getImageUrl, getJwtPayload } from "../../utils/appUtils";
import { CONTACT_TYPE_COLORS, CONTACT_TYPE_LABELS } from "../../utils/constants";
import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import GifLoader from "../../components/common/GifLoader";
import ContactAddEdit from "../../components/Contacts/ContactAddEdit";
import ContactAddList from "../../components/Contacts/ContactAddList";
import ContactBulkImport from "../../components/Contacts/ContactBulkImport";
import {
    fetchContacts,
    selectContacts,
    selectFetchContactsLoading,
    deleteContact,
    resetDeleteContactStatus,
    selectDeleteContactLoading,
    selectDeleteContactSuccess,
    selectDeleteContactError,
} from "../../redux/contactSlice";

const { Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

export default function Contacts() {
    const dispatch = useDispatch();
    const [modal, contextHolder] = Modal.useModal();
    const screens = useBreakpoint();

    // Redux Selectors
    const contacts = useSelector(selectContacts);
    const contactsLoading = useSelector(selectFetchContactsLoading);
    const deleteLoading = useSelector(selectDeleteContactLoading);
    const deleteSuccess = useSelector(selectDeleteContactSuccess);
    const deleteError = useSelector(selectDeleteContactError);

    // State variables
    const [openContact, setOpenContact] = useState(false);
    const [openList, setOpenList] = useState(false);
    const [openBulkImport, setOpenBulkImport] = useState(false);
    const [gifLoader, setGifLoader] = useState(false);

    const getListData = () => {
        const token = localStorage.getItem('authToken');
        const userPayload = getJwtPayload(token);
        if (userPayload) {
            dispatch(fetchContacts(userPayload.id));
        }
    };

    // Fetch contacts on mount
    useEffect(() => {
        getListData();
    }, [dispatch]);

    // Handle Delete Success/Error
    useEffect(() => {
        if (deleteSuccess) {
            getListData();
            dispatch(resetDeleteContactStatus());
        } else if (deleteError) {
            dispatch(resetDeleteContactStatus());
        }
    }, [deleteSuccess, deleteError, dispatch]);

    // Delete Contact Handler
    const handleDeleteContact = (contactId) => {
        modal.confirm({
            title: "Delete Contact",
            icon: null,
            content: (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <ExclamationCircleFilled style={{ color: "#faad14", fontSize: 18, marginTop: 2 }} />
                    <div>Are you sure you want to delete this contact? This action cannot be undone.</div>
                </div>
            ),
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            cancelButtonProps: { className: "draftBtn" },
            centered: true,
            onOk: () => {
                dispatch(deleteContact(contactId));
            },
        });
    };

    /**
     * Helper functions
     */
    // Columns configuration
    const columns = [
        {
            dataIndex: "media",
            width: 100,
            render: (media) => <Avatar src={media && media.length > 0 ? getImageUrl(media[media.length - 1]) : null} size={65} icon={<UserOutlined />} />,
        },
        {
            dataIndex: "contact_name",
            title: "Name",
            render: (text, record) => (
                <Flex vertical gap="4">
                    <div>
                        <Tag className={`eventTag ${CONTACT_TYPE_COLORS[record.contact_type] || "default"}`}>
                            {CONTACT_TYPE_LABELS[record.contact_type] || "Other"}
                        </Tag>
                    </div>
                    <Text>{text}</Text>
                </Flex>
            ),
        },
        {
            dataIndex: "email",
            title: "Email",
            render: (text) => <Text>{text}</Text>,
        },
        {
            width: 160,
            dataIndex: "mobile",
            title: "Phone",
        },
        {
            width: 100,
            align: "right",
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        size="small"
                        className="actionIcon"
                        icon={<EditIcon />}
                    />
                    <Button
                        type="text"
                        size="small"
                        className="actionIcon"
                        danger
                        icon={<TrashIcon />}
                        onClick={() => handleDeleteContact(record.id)}
                        loading={deleteLoading}
                    />
                </Space>
            ),
        },
    ];

    // Logic for subsets of contacts (Number() handles string/number mismatch from API)
    const familyContacts = (contacts || []).filter(c => Number(c.contact_type) === 1);
    const friendContacts = (contacts || []).filter(c => Number(c.contact_type) === 2);
    const workContacts = (contacts || []).filter(c => Number(c.contact_type) === 3);

    // Reusable table component
    const ContactTable = ({ data }) => (
        <div className="contactTblWrap">
            <Flex vertical gap={12}>
                <Flex justify="flex-end" gap={12}>
                    <Text strong>Total Contacts</Text>
                    <Tag className="eventTag pink" style={{ minWidth: 40, textAlign: "center" }}>
                        {data ? data.length : 0}
                    </Tag>
                </Flex>
                <Table
                    className="table-scroll-m"
                    rowSelection={{
                        type: "checkbox",
                        getCheckboxProps: () => ({ className: "checkboxStyle1" }),
                    }}
                    columns={columns}
                    dataSource={data || []}
                    pagination={false}
                    showHeader={false}
                    rowClassName="custom-row"
                    scroll={{ x: 640, y: 98 * 8 }}
                    rowKey={(record) => record.id || record.mobile}
                    loading={contactsLoading}
                />
            </Flex>
        </div>
    );

    return (
        <PageLayout title="Contacts">
            {gifLoader && (
                <div className="loader-fixed">
                    <GifLoader />
                </div>
            )}
            {contextHolder}
            {/* SEO HELMET WRAPPER */}
            <HelmetWrapper
                title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
                description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
                and city-specific visibility for organizers and users." 
                keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
                online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
                Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
                Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
            />
            <Content className={gifLoader ? "is-loading-blur" : ""}>
                <Flex justify="end" gap={16} vertical={!screens.md}>
                    <Button
                        type="primary"
                        size="large"
                        className="btnGradient"
                        onClick={() => setOpenContact(true)}
                    >
                        Create New Contact
                    </Button>

                    <Button
                        type="primary"
                        size="large"
                        className="btnGradient"
                        onClick={() => setOpenList(true)}
                    >
                        New Contact List
                    </Button>

                    <Button
                        type="primary"
                        size="large"
                        className="btnGradient"
                        onClick={() => setOpenBulkImport(true)}
                    >
                        Bulk Import
                    </Button>
                </Flex>

                <Tabs
                    defaultActiveKey="all"
                    className="eventTabs"
                    type="card"
                    items={[
                        {
                            key: "allContacts",
                            label: "All Contacts",
                            children: <div className="tabContentWrap"><ContactTable data={contacts} /></div>,
                        },
                        {
                            key: "family",
                            label: "Family",
                            children: <div className="tabContentWrap"><ContactTable data={familyContacts} /></div>,
                        },
                        {
                            key: "business",
                            label: "Work",
                            children: <div className="tabContentWrap"><ContactTable data={workContacts} /></div>,
                        },
                        {
                            key: "friends",
                            label: "Friends",
                            children: <div className="tabContentWrap"><ContactTable data={friendContacts} /></div>,
                        },
                    ]}
                />

                {/* CREATE CONTACT MODAL */}
                <ContactAddEdit 
                    openContact={openContact} 
                    setOpenContact={setOpenContact}
                    getListData={getListData}
                    setGifLoader={setGifLoader}
                />

                {/* CONTACT LIST MODAL */}
                <ContactAddList
                    openList={openList}
                    setOpenList={setOpenList}
                />

                {/* BULK IMPORT MODAL */}
                <ContactBulkImport
                    openBulkImport={openBulkImport}
                    setOpenBulkImport={setOpenBulkImport}
                />
            </Content>
        </PageLayout>
    );
}
